import React, { useState } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import MenuMobilePublic from './MenuMobilePublic';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import { ChevronDown, ArrowRight, LifeBuoy } from 'lucide-react';
import './LandingPage.css';
import './BlogPage.css';
import './HelpCenterPage.css';

// ─── Données FAQ ──────────────────────────────────────────────────────────────
const FAQ_CATEGORIES = [
  {
    id: 'plateforme',
    title: '📌 Plateforme KaneaBourse',
    items: [
      {
        q: "Qu'est-ce que Kaneabourse ?",
        a: "Kaneabourse est la première plateforme d'analyse quantitative et d'aide à la décision dédiée exclusivement à la BRVM. Nous centralisons des années de données financières et utilisons l'intelligence artificielle pour estimer la valeur fondamentale des actions, vous aidant ainsi à prendre des décisions d'investissement basées sur les chiffres et non sur les émotions.",
        time: "1 min"
      },
      {
        q: "Pouvez-vous investir mon argent pour moi ?",
        a: "Non. Kaneabourse est un fournisseur d'outils technologiques et d'analyses indépendant. Pour acheter ou vendre des actions, vous devez ouvrir un compte-titres et passer vos ordres via une Société de Gestion et d'Intermédiation (SGI) agréée par l'AMF-UMOA.",
        time: "1 min"
      },
      {
        q: "À qui s'adresse la plateforme ?",
        a: "KaneaBourse s'adresse aussi bien aux débutants (pour apprendre et comprendre le vrai potentiel des actions grâce à l'IA) qu'aux professionnels et SGI qui ont besoin d'outils quantitatifs poussés pour gagner un temps précieux dans leurs analyses.",
        time: "1 min"
      }
    ]
  },
  {
    id: 'brvm',
    title: '📈 La BRVM (Bourse Régionale)',
    items: [
      {
        q: "Qu'est-ce que la BRVM ?",
        a: "La Bourse Régionale des Valeurs Mobilières (BRVM) est le marché financier commun aux huit pays de l'UEMOA (Bénin, Burkina Faso, Côte d'Ivoire, Guinée-Bissau, Mali, Niger, Sénégal, Togo). Elle regroupe les plus grandes entreprises de la région, comme Sonatel, Ecobank ou Orange CI.",
        time: "1 min"
      },
      {
        q: "Pourquoi investir à la BRVM ?",
        a: "La BRVM offre historiquement des rendements de dividendes parmi les plus attractifs au monde (souvent supérieurs à 8% ou 10% annuels net), constituant un excellent moyen de construire un patrimoine ou de générer des revenus passifs réguliers sur le long terme.",
        time: "2 min"
      }
    ]
  },
  {
    id: 'dividendes',
    title: '💰 Les Dividendes',
    items: [
      {
        q: "Qu'est-ce qu'un dividende ?",
        a: "C'est la part des bénéfices nets qu'une entreprise décide de redistribuer à ses actionnaires. Si vous possédez l'action le jour précédant la date de détachement (ex-dividend date), vous recevrez automatiquement l'argent sur votre compte SGI quelques jours plus tard.",
        time: "1 min"
      },
      {
        q: "Comment Kaneabourse aide-t-il avec les dividendes ?",
        a: "Notre plateforme centralise le calendrier des paiements, l'historique des rendements sur 5 ans et estime surtout la pérennité du dividende pour éviter les \"pièges de valeur\" (yield traps) grâce à nos modèles d'analyse financière.",
        time: "1 min"
      }
    ]
  },
  {
    id: 'ia',
    title: '🤖 L\'Intelligence Artificielle',
    items: [
      {
        q: "Comment utilisez-vous l'Intelligence Artificielle ?",
        a: "L'IA de Kaneabourse lit et structure les rapports financiers officiels dès leur publication. Notre moteur croise ensuite 14 modèles de valorisation (DCF, Graham, Gordon-Shapiro, etc.) et alimente \"Nia\", votre copilote financier conversationnel disponible 24/7 pour répondre à toutes vos questions sur les marchés.",
        time: "2 min"
      },
      {
        q: "Pourquoi l'IA est-elle plus objective qu'une analyse humaine ?",
        a: "L'IA élimine l'ennemi numéro un de l'investisseur : l'émotion. Un humain peut s'attacher à une action, ignorer des signaux d'alerte (biais de confirmation) ou céder à la panique. Notre IA analyse froidement des millions de points de données (bilans, ratios de liquidité, momentum) de manière strictement mathématique.",
        time: "2 min"
      }
    ]
  },
  {
    id: 'glossaire',
    title: '📚 Glossaire',
    items: [
      {
        q: "AMF-UMOA",
        a: "L'Autorité des Marchés Financiers de l'UMOA. C'est le gendarme de la bourse qui régule et contrôle les acteurs du marché financier régional pour protéger l'épargne.",
        time: "1 min"
      },
      {
        q: "SGI (Société de Gestion et d'Intermédiation)",
        a: "Une banque d'affaires ou un courtier agréé. C'est le seul acteur autorisé à exécuter vos ordres d'achat et de vente d'actions à la BRVM.",
        time: "1 min"
      },
      {
        q: "Indice de Valeur Kanea (IVK)",
        a: "Un indicateur synthétique calculé par nos algorithmes croisant 14 modèles mathématiques pour estimer la valeur fondamentale (le « juste prix théorique ») d'une action, indépendamment de la spéculation du marché.",
        time: "2 min"
      }
    ]
  }
];

// ─── Composant Accordéon ──────────────────────────────────────────────────────
const FAQItem: React.FC<{ q: string; a: string; time: string }> = ({ q, a, time }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`help-faq-item ${isOpen ? 'help-faq-item--open' : ''}`}>
      <button className="help-faq-item__header" onClick={() => setIsOpen(!isOpen)} aria-expanded={isOpen}>
        <span className="help-faq-item__title">{q}</span>
        <div className="help-faq-item__meta">
          <span className="help-faq-item__time">{time}</span>
          <ChevronDown className="help-faq-item__icon" size={20} />
        </div>
      </button>
      <div className="help-faq-item__body" aria-hidden={!isOpen}>
        {a}
      </div>
    </div>
  );
};

// ─── Composant Principal ──────────────────────────────────────────────────────
const HelpCenterPage: React.FC = () => {
  usePageTitle("Centre d'aide");
  const [activeCategory, setActiveCategory] = useState(FAQ_CATEGORIES[0].id);

  const scrollToCategory = (id: string) => {
    setActiveCategory(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -120;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="help-page">

      {/* ── Header / Hero ── */}
      <header className="help-hero">
        <nav className="landing-nav">
          <Link to="/home" className="landing-nav-logo">
            <KaneaLogo variant="horizontal" title="KaneaBourse" markColor="#6C3FC4" textColor="var(--lp-text)" height={28} />
            <div className="landing-nav-logo-dot" />
          </Link>
          <ul className="landing-nav-links">
            <li><Link to="/home">Accueil</Link></li>
            <li><Link to="/blog">Publications</Link></li>
            <li><Link to="/help">Centre d'aide</Link></li>
          </ul>
          <div className="landing-nav-actions">
            <Link to="/login" className="btn-nav-login">Se connecter</Link>
            <Link to="/members/signup" className="btn-nav-signup">Compte gratuit</Link>
            <MenuMobilePublic liens={[{ label: "Accueil", to: '/home' }, { label: "Tarifs", to: '/home#tarifs' }, { label: "Outils gratuits", to: '/outils' }, { label: "Publications", to: '/blog' }, { label: "Centre d'aide", to: '/help' }]} />
          </div>
        </nav>

        <div className="help-hero__content">
          <span className="help-hero__pill">
            <LifeBuoy size={14} />
            Centre d'Aide & Documentation
          </span>
          <h1 className="help-hero__title">
            Comment pouvons-nous<br />vous <span className="help-hero__accent">aider</span> ?
          </h1>
          <p className="help-hero__sub">
            Retrouvez toutes les informations sur KaneaBourse, la réglementation, les dividendes et notre technologie d'Intelligence Artificielle.
          </p>
        </div>
      </header>

      {/* ── Layout Principal ── */}
      <div className="help-container">

        {/* Sidebar Navigation */}
        <aside className="help-sidebar">
          {FAQ_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`help-sidebar__link ${activeCategory === cat.id ? 'help-sidebar__link--active' : ''}`}
              onClick={() => scrollToCategory(cat.id)}
              style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', borderLeft: activeCategory === cat.id ? '3px solid var(--lp-purple)' : '3px solid transparent' }}
            >
              {cat.title}
            </button>
          ))}
        </aside>

        {/* Main Content / FAQ List */}
        <main className="help-main">
          {FAQ_CATEGORIES.map((category) => (
            <section key={category.id} id={category.id} className="help-category">
              <h2 className="help-category__title">{category.title}</h2>
              <div className="help-faq-list">
                {category.items.map((item, itemIndex) => (
                  <FAQItem key={itemIndex} q={item.q} a={item.a} time={item.time} />
                ))}
              </div>
            </section>
          ))}
        </main>
      </div>

      {/* ── Conversion Banner ── */}
      <section className="kanea-purple-banner">
        <div className="kanea-purple-banner__inner">
          <h2 className="kanea-purple-banner__title">
            Prêt à appliquer ces connaissances ?
          </h2>
          <p className="kanea-purple-banner__sub">
            Rejoignez les investisseurs qui utilisent l'IA pour analyser la BRVM de manière objective.
          </p>
          <div className="kanea-purple-banner__actions">
            <Link to="/members/signup" className="kanea-purple-banner__cta">
              Créer un compte gratuit <ArrowRight size={16} />
            </Link>
            <Link to="/login" className="kanea-purple-banner__ghost">
              Déjà membre ? Connexion
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer">
        <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 24, textAlign: 'center', fontSize: '0.75rem', color: '#64748B', borderBottom: '1px solid var(--lp-border)', marginBottom: 24 }}>
          <strong>Avertissement légal :</strong> KaneaBourse est une plateforme d'analyse de données et d'outils d'aide à la décision. Nous ne sommes ni une SGI (Société de Gestion et d'Intermédiation) ni un Conseiller en Investissement Financier agréé par l'AMF-UMOA. Les informations, analyses, et valeurs fondamentales fournies (y compris par l'IA) le sont à titre strictement informatif et éducatif. Elles ne constituent en aucun cas une recommandation d'achat ou de vente personnalisée. Tout investissement en bourse comporte un risque de perte en capital.
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div className="landing-footer-copy">
            © {new Date().getFullYear()} KaneaBourse — Tous droits réservés
          </div>
          <div className="landing-footer-links" style={{ display: 'flex', gap: 16 }}>
            <Link to="/terms">Conditions d'utilisation</Link>
            <Link to="/confidentialite">Confidentialité</Link>
            <a href="mailto:contact@kaneabourse.com">Contact</a>
            <a href="https://www.brvm.org" target="_blank" rel="noopener noreferrer">BRVM</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenterPage;
